/**
 * AdminMaintenance.js — Command Centre: live green/red panel of every
 * Maintenance Crew check + recent cycle history. Light theme, inside AdminLayout.
 */
import React, { useEffect, useState } from 'react';
import AdminLayout from '../../components/layout/AdminLayout';
import { Wrench, Loader2, CheckCircle, XCircle, RefreshCw, Gauge } from 'lucide-react';
import { apiClient } from '../../api/client';

const CREW_META = {
  db_sentry: { name: 'David', img: '/maintenance-team/david.jpg', role: 'Database Sentry', desc: 'MongoDB health & reachability' },
  mint_medic: { name: 'Mary', img: '/maintenance-team/mary.jpg', role: 'Mint Medic', desc: 'Repairs stuck NFT mints' },
  admin_guardian: { name: 'Paul', img: '/maintenance-team/paul.jpg', role: 'Admin Guardian', desc: 'Protects your admin access' },
  link_inspector: { name: 'Jess', img: '/maintenance-team/jess.jpg', role: 'Link Inspector', desc: 'Family link integrity' },
  inbox_watchdog: { name: 'Barbara', img: '/maintenance-team/barbara.jpg', role: 'Inbox Watchdog', desc: 'Help & bug report SLAs' },
  digest_courier: { name: 'Jamie', img: '/maintenance-team/jamie.jpg', role: 'Digest Courier', desc: 'Sunday parent digest + champion crowning' },
  sensitivity_sentinel: { name: 'Melissa', img: '/maintenance-team/melissa.jpg', role: 'Sensitivity Manager', desc: 'Handover & safety-trigger drift watch' },
  credit_sentinel: { name: 'Frank', img: '/maintenance-team/frank.jpg', role: 'Billing Manager', desc: 'API credits & quota watch — emails + texts you when a top-up is needed' },
};

const STATUS_COLORS = {
  green: { ring: 'border-emerald-200 bg-emerald-50', dot: 'bg-emerald-500', label: 'HEALTHY', text: 'text-emerald-600' },
  amber: { ring: 'border-amber-200 bg-amber-50', dot: 'bg-amber-500', label: 'SLOWING', text: 'text-amber-600' },
  red: { ring: 'border-red-200 bg-red-50', dot: 'bg-red-500', label: 'SLOW', text: 'text-red-600' },
  idle: { ring: 'border-gray-200 bg-gray-50', dot: 'bg-gray-400', label: 'QUIET — TOO FEW REPLIES TO RATE', text: 'text-gray-500' },
};

const fmtMs = (ms) => (ms >= 1000 ? `${(ms / 1000).toFixed(1)}s` : `${Math.round(ms)}ms`);

const LatencyMeter = () => {
  const [data, setData] = useState(null);
  const load = () => apiClient.request('/api/admin/logs/ava-latency').then(setData).catch(() => setData({ status: 'idle', last_hour: { count: 0, avg_ms: 0, p95_ms: 0 }, last_24h: { count: 0, avg_ms: 0, p95_ms: 0 }, trend: [] }));
  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []);

  if (!data) return null;
  const c = STATUS_COLORS[data.status] || STATUS_COLORS.idle;
  const trend = (data.trend || []).filter((t) => t.count > 0);
  const maxTrend = Math.max(...trend.map((t) => t.avg_ms), 1);
  return (
    <div className={`rounded-2xl p-5 border mb-8 ${c.ring}`} data-testid="latency-meter">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2.5">
          <Gauge size={18} className={c.text} />
          <p className="font-bold text-sm text-gray-900">AvA Reply Speed</p>
          <span className={`w-2 h-2 rounded-full ${c.dot} animate-pulse`} />
          <span className={`text-[10px] font-bold tracking-widest ${c.text}`} data-testid="latency-status">{c.label}</span>
        </div>
        <span className="text-[10px] text-gray-400">Healthy &lt; 3s · Slowing 3–5s · Slow &gt; 5s — auto-refreshes every 60s</span>
      </div>
      <div className="grid grid-cols-3 gap-3 mb-2">
        <div className="rounded-xl bg-white border border-gray-100 px-4 py-3">
          <p className="text-[10px] uppercase tracking-widest text-gray-400 mb-1">Avg (last hour)</p>
          <p className="text-xl font-bold text-gray-900" data-testid="latency-avg-hour">{data.last_hour.count ? fmtMs(data.last_hour.avg_ms) : '—'}</p>
          <p className="text-[10px] text-gray-400">{data.last_hour.count} replies</p>
        </div>
        <div className="rounded-xl bg-white border border-gray-100 px-4 py-3">
          <p className="text-[10px] uppercase tracking-widest text-gray-400 mb-1">Slowest 5% (p95)</p>
          <p className="text-xl font-bold text-gray-900" data-testid="latency-p95">{data.last_hour.count ? fmtMs(data.last_hour.p95_ms) : '—'}</p>
          <p className="text-[10px] text-gray-400">last hour</p>
        </div>
        <div className="rounded-xl bg-white border border-gray-100 px-4 py-3">
          <p className="text-[10px] uppercase tracking-widest text-gray-400 mb-1">Avg (24h)</p>
          <p className="text-xl font-bold text-gray-900" data-testid="latency-avg-24h">{data.last_24h.count ? fmtMs(data.last_24h.avg_ms) : '—'}</p>
          <p className="text-[10px] text-gray-400">{data.last_24h.count} replies</p>
        </div>
      </div>
      <p className="text-[10px] text-gray-400 mb-3">Contract: every AvA reply lands in under 3 seconds. The light turns red if sustained replies exceed 5s.</p>
      {trend.length >= 2 && (
        <div className="flex items-end gap-1 h-12" data-testid="latency-trend">
          {trend.map((t) => (
            <div key={t.hour} className="flex-1 max-w-[40px] rounded-t bg-gray-300 hover:bg-gray-400 transition-colors" title={`${t.hour}:00 — ${fmtMs(t.avg_ms)} avg (${t.count} replies)`} style={{ height: `${Math.max(8, (t.avg_ms / maxTrend) * 100)}%` }} />
          ))}
        </div>
      )}
    </div>
  );
};

const AdminMaintenance = () => {
  const [cycles, setCycles] = useState(null);
  const [trend, setTrend] = useState(null);
  const load = () => {
    apiClient.request('/api/maintenance/status').then(d => setCycles(d?.cycles || [])).catch(() => setCycles([]));
    apiClient.request('/api/maintenance/sentinel-trend').then(d => setTrend(d?.days || null)).catch(() => setTrend(null));
  };
  useEffect(() => { load(); }, []);

  const latest = cycles?.[0];
  return (
    <AdminLayout pageTitle="Maintenance Crew">
      <div className="max-w-5xl">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl flex items-center justify-center text-white" style={{ backgroundColor: '#F5831F' }}><Wrench size={20} /></div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Maintenance Crew</h1>
              <p className="text-sm text-gray-500">Eight always-on workers monitor, manage and self-repair every hour.</p>
            </div>
          </div>
          <button onClick={load} className="p-2.5 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-700" data-testid="maintenance-refresh"><RefreshCw size={16} /></button>
        </div>

        <LatencyMeter />

        {cycles === null ? (
          <Loader2 className="animate-spin" style={{ color: '#F5831F' }} size={26} />
        ) : !latest ? (
          <p className="text-gray-500">No cycles logged yet — the first runs within the hour.</p>
        ) : (
          <>
            <p className="text-xs text-gray-400 mb-3">Latest cycle: {new Date(latest.ran_at).toLocaleString()}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-10" data-testid="maintenance-panel">
              {Object.entries(latest.results || {}).map(([name, r]) => {
                const meta = CREW_META[name] || { name, img: null, role: name, desc: '' };
                return (
                  <div key={name} className={`rounded-2xl p-5 border flex items-start gap-4 bg-white ${r.ok ? 'border-emerald-200' : 'border-red-300 bg-red-50'}`} data-testid={`crew-${name}`}>
                    {meta.img && (
                      <img src={meta.img} alt={meta.name}
                        className={`w-16 h-16 rounded-2xl object-cover flex-shrink-0 border-2 ${r.ok ? 'border-emerald-400' : 'border-red-400'}`} />
                    )}
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        {r.ok ? <CheckCircle size={15} className="text-emerald-500 flex-shrink-0" /> : <XCircle size={15} className="text-red-500 flex-shrink-0" />}
                        <p className="font-bold text-sm text-gray-900 truncate">{meta.name} — {meta.role}</p>
                      </div>
                      <p className="text-[11px] text-gray-500 mb-1.5">{meta.desc}</p>
                      <p className="text-xs text-gray-500 font-mono">
                        {Object.entries(r).filter(([k, v]) => k !== 'ok' && typeof v !== 'object').map(([k, v]) => `${k}: ${v}`).join(' · ') || 'all clear'}
                      </p>
                      {name === 'sensitivity_sentinel' && trend && trend.length > 0 && (
                        <div className="mt-2.5" data-testid="sentinel-trend">
                          <div className="flex items-end gap-1 h-8">
                            {trend.map((d) => {
                              const max = Math.max(...trend.map((x) => x.handovers), 1);
                              return (
                                <div key={d.date} className="flex-1 max-w-[26px] flex flex-col items-center justify-end gap-0.5 h-full">
                                  <div
                                    className={`w-full rounded-t ${d.handovers > 0 ? 'bg-amber-400 hover:bg-amber-500' : 'bg-gray-200'} transition-colors`}
                                    title={`${d.date} — ${d.handovers} handovers · ${d.nudges} nudges · ${d.blocks} blocks`}
                                    style={{ height: d.handovers > 0 ? `${Math.max(8, Math.round((d.handovers / max) * 24))}px` : '3px' }}
                                  />
                                  <span className="text-[8px] text-gray-400 leading-none">{d.day[0]}</span>
                                </div>
                              );
                            })}
                          </div>
                          <p className="text-[9px] text-gray-400 mt-1">Handover triggers · last 7 days</p>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <h2 className="text-sm font-bold uppercase tracking-widest text-gray-400 mb-3">Recent cycles</h2>
            <ul className="space-y-1.5">
              {cycles.slice(0, 12).map(c => (
                <li key={c.cycle_id} className="flex items-center gap-3 text-xs px-4 py-2.5 rounded-xl bg-white border border-gray-200">
                  {c.healthy ? <CheckCircle size={13} className="text-emerald-500" /> : <XCircle size={13} className="text-red-500" />}
                  <span className="text-gray-600">{new Date(c.ran_at).toLocaleString()}</span>
                  <span className={`ml-auto font-bold ${c.healthy ? 'text-emerald-600' : 'text-red-600'}`}>{c.healthy ? 'HEALTHY' : 'ATTENTION'}</span>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminMaintenance;
